/*
 * Tykables — Back-in-Stock Notify-Me
 * -----------------------------------------------------------------------------
 * Custom element <tyk-notify-me> rendered by sections/tyk-main-product.liquid.
 * (Anonymous users get a "Log in to get notified" link inside the element rather
 * than the toggle button, so JS doesn't handle the unauthenticated path.)
 *
 * As of FIX-20260812-01 the PDP renders this element for EVERY product and
 * toggles its `hidden` attribute as the shopper switches variants, rather than
 * emitting it only when the first-resolved variant happens to be out of stock.
 * The element therefore has to be re-pointable: data-variant-id and
 * data-subscribed are observed attributes, and syncVariant() in the section
 * rewrites them on each variant change. Nothing here may cache those values.
 *
 * Flow on click:
 *   1. POST { variant_id, action: 'subscribe' | 'unsubscribe' } to the App
 *      Proxy /apps/notify endpoint. The app side writes to the customer's
 *      `custom.notify_variant_ids` metafield. As of 2026-08-28 this metafield
 *      is UI bookkeeping only (drives the persisted "already subscribed"
 *      badge across visits) — it is no longer what actually triggers a
 *      notification. See point 2a.
 *   2. In parallel:
 *      a. (subscribe only) POST directly to Klaviyo's Create Client Back in
 *         Stock Subscription endpoint (public key, safe to call from the
 *         browser — see KLAVIYO_PUBLIC_KEY below). This is what actually
 *         queues the shopper on Klaviyo's "Back In Stock Flow - Standard",
 *         which watches Klaviyo's own Shopify-synced inventory and sends the
 *         email when the variant restocks. Klaviyo has no unsubscribe API
 *         for this, so unsubscribe clicks skip this call entirely — see the
 *         2026-08-28 note further down on why the toggle-off UI is now
 *         cosmetic for the Klaviyo side.
 *      b. call OneSignal.User.addTag('notify_v_<variant_id>', '1') so a
 *         push-dispatch path can target a OneSignal segment later. Tag is
 *         removed on unsubscribe. (The push-dispatch backend itself has been
 *         non-functional since launch — invalid OneSignal REST key — so this
 *         currently just maintains the tag for whenever that gets fixed.)
 *   3. Swap the button to the opposite state on success; surface a
 *      friendly toast in the [data-tyk-notify-msg] region on failure.
 *
 * All three calls are best-effort in parallel so the user only opts in once.
 * The metafield write is still treated as the source of truth for the
 * TOGGLE UI (it's the only one of the three with a read-back), but the
 * Klaviyo call is what actually gets an email sent — see docs/BACK-IN-STOCK.md
 * for the full, updated architecture writeup.
 *
 * Push permission is requested by OneSignal's existing soft-prompt flow on
 * first interaction — we don't trigger the native browser prompt directly,
 * because doing so without the soft pre-prompt is a Chrome anti-pattern that
 * burns the permission for the whole site if the user clicks Block.
 *
 * 2026-08-28: added the direct-to-Klaviyo subscribe call (see
 * _subscribeKlaviyo below). Klaviyo's back-in-stock subscription API has no
 * unsubscribe/cancel endpoint — per Klaviyo's docs, a subscription is
 * automatically cleared once the shopper is notified, and duplicate
 * subscribe calls for the same profile+variant are silently no-ops. So the
 * "unsubscribe" toggle here only ever undoes our own metafield bookkeeping
 * and the OneSignal tag; it cannot retract a Klaviyo subscription that's
 * already been queued. In practice this only matters if someone subscribes
 * and un-subscribes before the item restocks, which should be rare enough
 * not to block shipping this — flag if it becomes a real complaint.
 *
 * See docs/BACK-IN-STOCK.md for the full API contract this script depends on.
 */
(function () {
  'use strict';

  if (customElements.get('tyk-notify-me')) return;

  // Klaviyo account public API key ("company_id" in their API docs). Public
  // keys are safe to embed client-side by design — this is the same key
  // already used elsewhere in the account (e.g. the legacy Flow config).
  // If this ever rotates: Klaviyo admin -> Settings -> API Keys -> Public.
  var KLAVIYO_PUBLIC_KEY = 'V9B8eP';
  var KLAVIYO_BACK_IN_STOCK_ENDPOINT =
    'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY;

  customElements.define(
    'tyk-notify-me',
    class TykNotifyMe extends HTMLElement {
      // The PDP renders this element once and re-points it at whichever
      // variant the shopper selects (see syncVariant() in
      // sections/tyk-main-product.liquid). Observing these two attributes is
      // what makes that swap take effect — without it the element stayed
      // pinned to whatever variant happened to be rendered first, so a
      // shopper who picked a different sold-out size subscribed to the wrong
      // one.
      static get observedAttributes() {
        return ['data-variant-id', 'data-subscribed'];
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return;
        // Fires before connectedCallback for the initial attributes; the
        // guard below keeps that a no-op (nothing is bound yet).
        if (!this.toggleBtn) return;

        if (name === 'data-variant-id') {
          // Any in-flight message belongs to the previous variant.
          if (this.msg) this.msg.hidden = true;
          this._renderState();
        }
        if (name === 'data-subscribed') {
          this._renderState();
        }
      }

      connectedCallback() {
        this.toggleBtn = this.querySelector('[data-tyk-notify-toggle]');
        this.label = this.querySelector('[data-tyk-notify-label]');
        this.msg = this.querySelector('[data-tyk-notify-msg]');

        // The element renders without a toggle button when the customer
        // isn't logged in (the Liquid template emits a login link instead).
        // Bail in that case — there's nothing to bind.
        if (!this.toggleBtn) return;

        this._onClick = this._onClick.bind(this);
        this.toggleBtn.addEventListener('click', this._onClick);
        this._renderState();
      }

      disconnectedCallback() {
        if (this.toggleBtn && this._onClick) {
          this.toggleBtn.removeEventListener('click', this._onClick);
        }
      }

      // Read straight off the dataset rather than caching in
      // connectedCallback — these change as the shopper switches variants.
      get variantId() {
        return this.dataset.variantId;
      }

      // Routed through the existing /apps/pwa App Proxy on the
      // tykables-theme Shopify app — that's the same proxy that serves
      // the PWA service worker. A single Cloudflare Worker handles both
      // (see workers/backend.js in the app project, deployed per
      // docs/DEPLOY-BACK-IN-STOCK.md).
      get endpoint() {
        return this.dataset.endpoint || '/apps/pwa/notify';
      }

      get isSubscribed() {
        return this.dataset.subscribed === 'true';
      }

      get customerEmail() {
        return this.dataset.customerEmail || '';
      }

      set isSubscribed(value) {
        this.dataset.subscribed = value ? 'true' : 'false';
      }

      _onClick(evt) {
        evt.preventDefault();
        if (this.toggleBtn.classList.contains('is-loading')) return;

        var subscribing = !this.isSubscribed;
        // Pin the variant for the duration of this request — the shopper can
        // switch sizes while it's in flight, which rewrites data-variant-id.
        var clickedVariantId = this.variantId;
        this._setLoading(true);

        // Both channels are best-effort. If push fails (user denied,
        // OneSignal not loaded, etc.) we still want the metafield write to
        // succeed so the email path works. We surface a single error only
        // if the metafield write itself fails — the source of truth.
        var metafieldP = this._writeMetafield(subscribing, clickedVariantId);
        var pushP = this._updatePushTag(subscribing, clickedVariantId).catch(function (err) {
          // Non-fatal — email still works. Log for diagnostics.
          // eslint-disable-next-line no-console
          console.warn('[tyk-notify-me] push tag update failed:', err);
        });
        // Klaviyo has no unsubscribe endpoint for back-in-stock subscriptions
        // (see file header, 2026-08-28 note) — only fire this on subscribe.
        var klaviyoP = subscribing
          ? this._subscribeKlaviyo(clickedVariantId).catch(function (err) {
              // Non-fatal for the UI (metafield bookkeeping still succeeds),
              // but this IS the call that actually gets an email sent, so log
              // loudly for diagnostics.
              // eslint-disable-next-line no-console
              console.error('[tyk-notify-me] Klaviyo back-in-stock subscribe failed:', err);
            })
          : Promise.resolve();

        var self = this;
        Promise.all([metafieldP, pushP, klaviyoP])
          .then(function () {
            // The event is keyed to the variant that was actually clicked, so
            // it's always safe to fire — the PDP section script uses it to keep
            // its in-memory list of subscribed variant ids current.
            self.dispatchEvent(
              new CustomEvent('tyk:notify-change', {
                bubbles: true,
                detail: { variantId: clickedVariantId, subscribed: subscribing },
              })
            );
            // The UI writes are NOT safe unconditionally. If the shopper picked
            // a different size while this request was in flight, syncVariant()
            // has already re-pointed the element and rewritten data-subscribed
            // for the new variant — clobbering it here would show "You'll be
            // notified" on a size they never subscribed to, and the next click
            // would POST an unsubscribe for it.
            if (self.variantId !== clickedVariantId) return;
            self.isSubscribed = subscribing;
            self._renderState();
            self._showMsg(
              subscribing
                ? "We'll let you know the moment it's back."
                : "Notifications turned off for this size.",
              'ok'
            );
          })
          .catch(function (err) {
            // eslint-disable-next-line no-console
            console.error('[tyk-notify-me] subscribe failed:', err);
            // Same guard — don't surface size A's error under size B.
            if (self.variantId !== clickedVariantId) return;
            self._showMsg(
              "Couldn't save your preference. Please try again.",
              'err'
            );
          })
          .finally(function () {
            self._setLoading(false);
          });
      }

      // -- Channel: email (customer metafield via App Proxy) ---------------
      _writeMetafield(subscribing, variantId) {
        var targetVariantId = variantId || this.variantId;
        return fetch(this.endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            variant_id: targetVariantId,
            product_id: this.dataset.productId,
            product_handle: this.dataset.productHandle,
            product_title: this.dataset.productTitle,
            variant_title: this.dataset.variantTitle,
            action: subscribing ? 'subscribe' : 'unsubscribe',
          }),
        }).then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json().catch(function () { return {}; });
        });
      }

      // -- Channel: Klaviyo native back-in-stock (actual email delivery) ---
      // Calls Klaviyo's public, browser-safe "Create Client Back in Stock
      // Subscription" endpoint directly — no backend involved. This queues
      // the shopper on Klaviyo's "Back In Stock Flow - Standard", which
      // watches Klaviyo's Shopify-synced catalog inventory and sends the
      // restock email itself. See docs/BACK-IN-STOCK.md.
      _subscribeKlaviyo(variantId) {
        var targetVariantId = variantId || this.variantId;
        var email = this.customerEmail;
        if (!email) {
          // Shouldn't happen — the toggle button only renders for logged-in
          // customers, and data-customer-email is sourced from customer.email
          // in Liquid whenever the toggle is present. Bail rather than send
          // Klaviyo a request with no profile identifier.
          return Promise.reject(new Error('no customer email available'));
        }

        var payload = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email },
                },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: '$shopify:::$default:::' + targetVariantId,
                },
              },
            },
          },
        };

        return fetch(KLAVIYO_BACK_IN_STOCK_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify(payload),
        }).then(function (res) {
          // Success is a 202 with no body. Klaviyo also treats a duplicate
          // subscribe (same profile + variant) as a silent no-op, which
          // surfaces as a normal success response, not an error — so we
          // don't need to track "already subscribed" state before calling.
          if (!res.ok && res.status !== 202) {
            return res.text().then(function (text) {
              throw new Error('Klaviyo HTTP ' + res.status + ': ' + text);
            });
          }
        });
      }

      // -- Channel: push (OneSignal tag) -----------------------------------
      _updatePushTag(subscribing, variantId) {
        var tagKey = 'notify_v_' + (variantId || this.variantId);

        // OneSignalDeferred is set up by assets/onesignal-init.js. If push
        // is disabled in theme settings, the deferred queue won't drain,
        // so we resolve immediately and the tag write is a no-op.
        return new Promise(function (resolve, reject) {
          if (typeof window.OneSignalDeferred === 'undefined') {
            return resolve(); // push not initialized — email-only path
          }
          window.OneSignalDeferred.push(async function (OneSignal) {
            try {
              if (subscribing) {
                // Adding the tag implicitly opts the user into push if
                // they haven't already accepted. OneSignal's own soft
                // prompt (configured in onesignal-init.js) handles the
                // permission UI — we don't call requestPermission here
                // because doing so without the pre-prompt burns the
                // permission permanently if the user clicks Block.
                await OneSignal.User.addTag(tagKey, '1');
              } else {
                await OneSignal.User.removeTag(tagKey);
              }
              resolve();
            } catch (err) {
              reject(err);
            }
          });
        });
      }

      // -- UI helpers ------------------------------------------------------
      _setLoading(loading) {
        this.toggleBtn.classList.toggle('is-loading', loading);
        this.toggleBtn.disabled = loading;
      }

      _renderState() {
        if (!this.toggleBtn) return;
        // Copy comes from the Liquid template via data-cta-* so the paused
        // lifecycle wording ("back in production") survives a variant swap
        // instead of reverting to the generic string.
        if (this.isSubscribed) {
          this.toggleBtn.classList.add('is-subscribed');
          this.toggleBtn.classList.remove('tbtn--blue');
          this.toggleBtn.classList.add('tbtn--white');
          this.toggleBtn.setAttribute('aria-pressed', 'true');
          if (this.label) {
            this.label.textContent = this.dataset.ctaSubscribed || "✓ You'll be notified";
          }
        } else {
          this.toggleBtn.classList.remove('is-subscribed');
          this.toggleBtn.classList.remove('tbtn--white');
          this.toggleBtn.classList.add('tbtn--blue');
          this.toggleBtn.setAttribute('aria-pressed', 'false');
          if (this.label) {
            this.label.textContent = this.dataset.ctaSubscribe || '🔔 Notify me when back';
          }
        }
      }

      _showMsg(text, kind) {
        if (!this.msg) return;
        this.msg.textContent = text;
        this.msg.classList.remove('is-ok', 'is-err');
        if (kind === 'ok') this.msg.classList.add('is-ok');
        if (kind === 'err') this.msg.classList.add('is-err');
        this.msg.hidden = false;
        // Auto-dismiss success after 4s; keep errors visible.
        if (kind === 'ok') {
          var msg = this.msg;
          clearTimeout(this._msgT);
          this._msgT = setTimeout(function () {
            msg.hidden = true;
          }, 4000);
        }
      }
    }
  );
})();
